import React, { useMemo, useState } from 'react';
import { InputActionMeta } from 'react-select';
import {
  FieldType,
  INestedFieldQueryQuery,
  INestedFieldQueryQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { usePanelStateContext } from '../context';
import { useQuery } from '@apollo/client';
import { IReactSelectOption } from '../../../../utils/types';
import { isNil } from 'lodash';

import Loading from '../panel/Loading';
import nestedFieldQuery from '../../../../pages/Formulas/apollo/nestedFieldQuery';
import {
  CreatableReactSelectMenu,
  ReactSelectMenu,
} from '../../../Dropdown/StyledReactSelect';
import {
  ActionRow,
  ErrorMessageRow,
  NestedFieldDetailsForm,
} from '../EntitySelectionPanelUI';
import { PrimaryButton } from '../../../Button';
import { IInlineBranchNode, INestedFieldEntity } from '../types';
import { BasicSingleValue } from '../../../Dropdown/StyledReactSelect/SelectUtils';
import {
  convertToOptions,
  extractQueryOptions,
  findOption,
  nestedFieldReactSelectStyles,
} from './nestedFieldUtils';
import { createRefreshableMenuComponent } from '../../../DataRefresh/RefreshableMenuList';

interface IProps {
  entity: INestedFieldEntity;
  node: IInlineBranchNode;
}

const NestedFieldDetails = ({ node, entity }: IProps) => {
  const {
    schemaId,
    tags,
    getNestedFieldState,
    setNestedFieldState,
    getValidatedNestedField,
    selectNodeHandler,
  } = usePanelStateContext();
  const [term, setTerm] = useState('');
  const [nestedField, setNestedField] = useState<
    INestedFieldQueryQuery['nestedField'] | null
  >(null);

  const nestedFieldFormState = getNestedFieldState(entity);

  const queryOptions = extractQueryOptions(tags);
  const field = entity.field;
  const { loading, refetch } = useQuery<
    INestedFieldQueryQuery,
    INestedFieldQueryQueryVariables
  >(nestedFieldQuery, {
    variables: {
      term,
      field: field.key,
      schemaId,
      includeValues: [],
      ...queryOptions,
    },
    onCompleted: (data) => setNestedField(data.nestedField),
  });

  const RefreshableMenu = useMemo(
    () =>
      createRefreshableMenuComponent<IReactSelectOption, false, never>({
        field,
        schemaId,
        onRefreshEnd: refetch,
      }),
    [field, refetch, schemaId],
  );

  if (isNil(nestedField)) {
    return <Loading />;
  } else {
    const keyOptions = convertToOptions(nestedField.keys);
    let keyValue = findOption(keyOptions, nestedFieldFormState.key);
    // append creatable option if needed
    if (!isNil(nestedFieldFormState.key) && isNil(keyValue)) {
      const creatableOption: IReactSelectOption = {
        value: nestedFieldFormState.key,
        label: nestedFieldFormState.key,
      };
      keyValue = creatableOption;
      keyOptions.push(creatableOption);
    }
    const typeOptions = convertToOptions(nestedField.types);
    const typeValue = findOption(typeOptions, nestedFieldFormState.type);

    const isValid = !isNil(getValidatedNestedField(entity));

    const keyOrPathLabel =
      field.type === FieldType.Json ? 'Custom key or JSON Path' : 'Key';
    return (
      <NestedFieldDetailsForm>
        <CreatableReactSelectMenu
          // this component is inside scrollable container which doesn't
          // allow menu list to overflow outside it. Render the menu list
          // at the root of the document to escape.
          // It also requires adjusting the zIndex of the menu portal
          // be above the Popover via `styles`
          menuPortalTarget={document.body}
          options={keyOptions}
          value={keyValue}
          isLoading={loading}
          onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
            if (actionMeta.action === 'input-change') {
              setTerm(newValue);
            }
          }}
          onChange={(option) => {
            setTerm('');
            setNestedFieldState(entity, (draft) => {
              draft.key = option?.value;
              draft.error = null;
            });
          }}
          onCreateOption={(inputValue) => {
            setTerm('');
            setNestedFieldState(entity, (draft) => {
              draft.key = inputValue;
              draft.error = null;
            });
          }}
          components={{
            SingleValue: BasicSingleValue<IReactSelectOption, false, never>,
            Menu: RefreshableMenu,
          }}
          placeholder={keyOrPathLabel}
          menuPlacement="auto"
          maxMenuHeight={180}
          menuShouldScrollIntoView={false}
          styles={nestedFieldReactSelectStyles}
        />
        <ReactSelectMenu
          // this component is inside scrollable container which doesn't
          // allow menu list to overflow outside it. Render the menu list
          // at the root of the document to escape.
          // It also requires adjusting the zIndex of the menu portal
          // be above the Popover via `styles`
          menuPortalTarget={document.body}
          value={typeValue}
          isDisabled={loading}
          options={typeOptions}
          styles={nestedFieldReactSelectStyles}
          onChange={(option) =>
            setNestedFieldState(entity, (draft) => {
              draft.type = option?.value;
              draft.error = null;
            })
          }
          placeholder={'Cast to type'}
        />
        {!isNil(nestedFieldFormState.error) && (
          <ErrorMessageRow>{nestedFieldFormState.error}</ErrorMessageRow>
        )}
        <ActionRow>
          <PrimaryButton
            type="button"
            disabled={!isValid}
            onClick={() => selectNodeHandler(node)}
            trackContext="entity selection"
            trackAction="nested field confirm"
          >
            Confirm
          </PrimaryButton>
        </ActionRow>
      </NestedFieldDetailsForm>
    );
  }
};
export default NestedFieldDetails;
